import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  Globe,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Smartphone,
  Image as ImageIcon,
  BarChart3,
  FileText,
  Gauge,
  Link,
  Languages,
  Timer,
  Navigation,
  Share2,
  Eye,
  Hash,
} from "lucide-react";

import SEOLoadingState from "@/components/seo/SEOLoadingState";
import SEOErrorState from "@/components/seo/SEOErrorState";
import { useSEOAudit } from "@/hooks/useSEOAudit";
import { ComprehensiveEnhancementAudit, PageAnalysis } from "@/types/seo";
import { useBrand } from "@/contexts/brand-context";

const ITEMS_PER_PAGE = 10;

export default function Enhancements() {
  const {
    loading,
    error,
    enhancementAudit,
    enhancementLoading,
    enhancementError,
    fetchEnhancementAudit,
  } = useSEOAudit();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIssueType, setSelectedIssueType] = useState<string>("all");

  const { currentBrand } = useBrand();
  const lastRequestedBrandId = useRef<string | null>(null);

  // Helper functions for issue categorization and suggestions
  const getIssueIcon = (issue: string) => {
    if (
      issue.includes("Meta") ||
      issue.includes("Title") ||
      issue.includes("Description")
    ) {
      return <FileText className="h-4 w-4 text-blue-500" />;
    } else if (
      issue.includes("Speed") ||
      issue.includes("Load") ||
      issue.includes("Performance")
    ) {
      return <Gauge className="h-4 w-4 text-green-500" />;
    } else if (
      issue.includes("Mobile") ||
      issue.includes("Responsive") ||
      issue.includes("viewport")
    ) {
      return <Smartphone className="h-4 w-4 text-purple-500" />;
    } else if (issue.includes("Image") || issue.includes("Alt")) {
      return <ImageIcon className="h-4 w-4 text-orange-500" />;
    } else if (issue.includes("canonical") || issue.includes("Canonical")) {
      return <Link className="h-4 w-4 text-indigo-500" />;
    } else if (
      issue.includes("Open Graph") ||
      issue.includes("Twitter") ||
      issue.includes("social")
    ) {
      return <Share2 className="h-4 w-4 text-pink-500" />;
    } else if (issue.includes("language") || issue.includes("Language")) {
      return <Languages className="h-4 w-4 text-cyan-500" />;
    } else if (
      issue.includes("H1") ||
      issue.includes("heading") ||
      issue.includes("Heading")
    ) {
      return <Hash className="h-4 w-4 text-teal-500" />;
    } else if (issue.includes("link") || issue.includes("Link")) {
      return <ExternalLink className="h-4 w-4 text-violet-500" />;
    } else if (issue.includes("breadcrumb") || issue.includes("Breadcrumb")) {
      return <Navigation className="h-4 w-4 text-emerald-500" />;
    } else if (
      issue.includes("Content") ||
      issue.includes("content") ||
      issue.includes("read")
    ) {
      return <Eye className="h-4 w-4 text-slate-500" />;
    } else {
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  const getSuggestionForIssue = (issue: string): string => {
    switch (issue) {
      case "Missing meta description":
        return "Add a concise, compelling meta description (120-160 characters) that accurately summarizes the page content and includes relevant keywords.";
      case "Meta description too long (>160 characters)":
        return "Shorten your meta description to under 160 characters to prevent truncation in search results.";
      case "Meta description too short (<120 characters)":
        return "Expand your meta description to at least 120 characters to provide more context to users and search engines.";
      case "Missing page title":
        return "Add a unique, descriptive page title that includes your primary keyword and accurately describes the page content.";
      case "Title too long (>60 characters)":
        return "Shorten your title to 30-60 characters to ensure it displays properly in search results. Focus on including your most important keywords near the beginning.";
      case "Title too short (<30 characters)":
        return "Expand your title to at least 30 characters to provide more context and include relevant keywords.";
      case "Images missing alt attributes":
        return "Add descriptive alt text to all images that accurately describes what they show. Include relevant keywords where appropriate but avoid keyword stuffing.";
      case "Large unoptimized images detected":
        return "Optimize large images by compressing them, using modern formats (WebP, AVIF), and implementing responsive images with appropriate sizing.";
      case "Slow loading time (>3 seconds)":
        return "Optimize images, enable browser caching, minify CSS/JS files, use a CDN, and consider server-side optimizations to improve page load speed.";
      case "Page loading could be faster (>2 seconds)":
        return "Further optimize page speed by compressing resources, reducing HTTP requests, and implementing performance best practices.";
      case "Missing mobile viewport meta tag":
        return 'Add a viewport meta tag in your HTML head: <meta name="viewport" content="width=device-width, initial-scale=1">.';
      case "Content too short (<300 words)":
        return "Expand your content to at least 300-500 words with valuable, relevant information that addresses user search intent.";
      case "Content could be longer for better SEO (recommended 500+ words)":
        return "Consider expanding your content to 500+ words to provide more comprehensive information and improve SEO performance.";
      case "Missing H1 tag":
        return "Add a single H1 heading that clearly describes the page topic and includes your primary keyword.";
      case "Multiple H1 tags found (should have only one)":
        return "Use only one H1 tag per page. Convert additional H1 tags to H2 or H3 tags to maintain proper heading hierarchy.";
      case "H3 tag used without H2 (poor heading hierarchy)":
        return "Maintain proper heading hierarchy by using H2 tags before H3 tags. Structure your headings logically from H1 to H6.";
      case "Missing structured data markup":
        return "Implement appropriate schema markup (JSON-LD format) to help search engines better understand your content type and display rich results.";
      case "Missing canonical URL":
        return "Add a canonical link tag to specify the preferred version of pages and prevent duplicate content issues.";
      case "Missing Open Graph meta tags":
        return "Add Open Graph meta tags (og:title, og:description, og:image, og:url) to improve how your content appears when shared on social media.";
      case "Missing Twitter Card meta tags":
        return "Add Twitter Card meta tags (twitter:card, twitter:title, twitter:description, twitter:image) to optimize how your content appears on Twitter.";
      case "Missing language declaration (lang attribute)":
        return 'Add a lang attribute to your HTML tag (e.g., <html lang="en">) to help search engines and assistive technologies understand the page language.';
      case "Duplicate title tag (used on multiple pages)":
        return "Create unique title tags for each page that accurately describe the specific page content and include relevant keywords.";
      case "Duplicate meta description (used on multiple pages)":
        return "Write unique meta descriptions for each page that summarize the specific content and include relevant keywords.";
      case "No internal links found":
        return "Add internal links to other relevant pages on your website to improve navigation and help search engines understand your site structure.";
      case "Very few internal links (improve internal linking)":
        return "Increase internal linking by adding 2-5 relevant links to other pages on your website to improve user navigation and SEO.";
      case 'Most external links missing rel="nofollow" attribute':
        return 'Add rel="nofollow" attributes to external links to prevent passing link equity to other websites, unless the links are editorial endorsements.';
      case "Content too short for meaningful engagement (less than 2 min read)":
        return "Expand your content to provide more value and increase engagement time. Aim for at least 2-3 minutes of reading time.";
      case "Content very long (consider breaking into multiple pages)":
        return "Consider breaking very long content into multiple pages or sections to improve user experience and reduce bounce rate.";
      case "Missing breadcrumb navigation":
        return "Implement breadcrumb navigation to help users understand their location on your site and improve SEO with structured data.";
      case "Long content without images (consider adding relevant images)":
        return "Add relevant images, charts, or graphics to break up long text content and improve user engagement and experience.";
      case "URL too long (over 100 characters)":
        return "Shorten your URL to under 100 characters by removing unnecessary words and parameters while keeping it descriptive.";
      default:
        return "Review this issue and implement best practices to improve your SEO performance.";
    }
  };

  // Automatically fetch enhancement audit data when component mounts
  useEffect(() => {
    // Only auto-fetch if we have a brand and haven't already requested for this brand
    if (
      currentBrand?.id &&
      !enhancementLoading &&
      !enhancementAudit &&
      !loading &&
      !error &&
      lastRequestedBrandId.current !== currentBrand.id
    ) {
      lastRequestedBrandId.current = currentBrand.id;
      fetchEnhancementAudit();
    }
    // Reset the flag if the brand changes
    if (
      lastRequestedBrandId.current &&
      currentBrand?.id !== lastRequestedBrandId.current
    ) {
      lastRequestedBrandId.current = null;
    }
  }, [
    currentBrand?.id,
    enhancementLoading,
    enhancementAudit,
    loading,
    error,
    fetchEnhancementAudit,
  ]);

  const auditData = enhancementAudit as ComprehensiveEnhancementAudit | null;

  // Filter pages with issues based on selected issue type
  const filteredPages = useMemo(() => {
    if (!auditData?.siteAnalysis?.pages) return [];

    const pagesWithIssues = auditData.siteAnalysis.pages.filter(
      (page: PageAnalysis) =>
        page.issues && Array.isArray(page.issues) && page.issues.length > 0,
    );

    if (selectedIssueType === "all") {
      return pagesWithIssues;
    }

    return pagesWithIssues.filter(
      (page: PageAnalysis) =>
        page.issues &&
        Array.isArray(page.issues) &&
        page.issues.some((issue: string) => issue === selectedIssueType),
    );
  }, [auditData?.siteAnalysis?.pages, selectedIssueType]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPages.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedPages = filteredPages.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIssueType]);

  // Reset pagination and filters when enhancement audit changes (brand switch)
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIssueType("all");
  }, [enhancementAudit]);

  // Show loading state when either main audit or enhancement audit is loading
  if (loading || enhancementLoading) {
    return (
      <SEOLoadingState message="Analyzing your website for enhancement opportunities..." />
    );
  }

  // Show error state only if there's an actual error and we're not in a loading state
  if ((error || enhancementError) && !loading && !enhancementLoading) {
    return (
      <SEOErrorState
        title="Enhancement Analysis Failed"
        message={error || enhancementError || "Unknown error"}
      />
    );
  }

  // If no audit data is available yet, but we're not in an error state, show the start analysis UI
  if (!auditData) {
    return (
      <div className="container space-y-6 py-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight mb-4">
            SEO Issues Analysis
          </h1>
          <p className="text-muted-foreground mb-6">
            Get comprehensive SEO analysis and identify issues across your
            website
          </p>
          <Button onClick={() => fetchEnhancementAudit()}>
            Start Issues Analysis
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-7xl mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            SEO Enhancements
          </h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive analysis of SEO issues and optimization opportunities
            across your website
          </p>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
          <Badge variant="secondary" className="text-sm">
            Last analyzed:{" "}
            {auditData?.overview?.auditTimestamp
              ? new Date(auditData.overview.auditTimestamp).toLocaleDateString()
              : "N/A"}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchEnhancementAudit(undefined, true)}
            disabled={
              !!(
                enhancementLoading ||
                (auditData?.overview?.auditTimestamp &&
                  new Date().getTime() -
                    new Date(auditData.overview.auditTimestamp).getTime() <
                    24 * 60 * 60 * 1000)
              )
            }
            title={
              auditData?.overview?.auditTimestamp &&
              new Date().getTime() -
                new Date(auditData.overview.auditTimestamp).getTime() <
                24 * 60 * 60 * 1000
                ? "Refresh available after 24 hours"
                : "Refresh analysis"
            }
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${enhancementLoading ? "animate-spin" : ""}`}
            />
            {enhancementLoading ? "Analyzing..." : "Refresh Analysis"}
          </Button>
        </div>
      </div>

      {/* Enhancement Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Total Pages
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-blue-600">
                {auditData?.overview?.analyzedPages || 0}
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">analyzed</div>
                <div className="text-xs text-slate-400">pages</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Pages with Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-amber-600">
                {auditData?.overview?.pagesWithIssues || 0}
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">need</div>
                <div className="text-xs text-slate-400">attention</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-red-500" />
              Total Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-red-600">
                {auditData?.overview?.totalIssues || 0}
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">issues</div>
                <div className="text-xs text-slate-400">found</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20" />
          <CardHeader className="relative pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Issue Types
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-emerald-600">
                {Object.keys(auditData?.overview?.issueTypeCounts || {}).length}
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">unique</div>
                <div className="text-xs text-slate-400">types</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Most Common Issues */}
      {auditData?.overview?.issueTypeCounts &&
        Object.keys(auditData?.overview?.issueTypeCounts || {}).length > 0 && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Most Common Issues
              </CardTitle>
              <CardDescription>
                Click on an issue type to filter pages and see specific
                recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(auditData.overview.issueTypeCounts || {})
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 8) // Show top 8 issues to avoid overcrowding
                  .map(([issue, count]) => {
                    const isSelected = selectedIssueType === issue;
                    const percentage = Math.round(
                      (count / (auditData.overview.totalPages || 1)) * 100,
                    );

                    return (
                      <div
                        key={issue}
                        className={`group relative overflow-hidden rounded-lg border cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 shadow-sm"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm"
                        }`}
                        onClick={() =>
                          setSelectedIssueType(isSelected ? "all" : issue)
                        }
                      >
                        <div className="flex items-center p-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div
                              className={`p-2 rounded-lg ${isSelected ? "bg-blue-100 dark:bg-blue-900/50" : "bg-slate-100 dark:bg-slate-800"}`}
                            >
                              {getIssueIcon(issue)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                                  {issue}
                                </h4>
                                {isSelected && (
                                  <Badge variant="default" className="text-xs">
                                    Active Filter
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
                                <span className="font-medium">
                                  {count} pages affected
                                </span>
                                <span>({percentage}% of all pages)</span>
                              </div>
                            </div>
                          </div>
                          <div
                            className={`text-right ${isSelected ? "text-blue-600 dark:text-blue-400" : "text-slate-500 dark:text-slate-400"}`}
                          >
                            <div className="text-2xl font-bold">{count}</div>
                            <div className="text-xs">pages</div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 dark:bg-slate-800">
                          <div
                            className={`h-full transition-all duration-300 ${
                              isSelected ? "bg-blue-500" : "bg-amber-400"
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>

              {Object.keys(auditData.overview.issueTypeCounts || {}).length >
                8 && (
                <div className="mt-4 text-center">
                  <Badge variant="outline" className="text-xs">
                    +
                    {Object.keys(auditData.overview.issueTypeCounts || {})
                      .length - 8}{" "}
                    more issue types
                  </Badge>
                </div>
              )}

              {selectedIssueType !== "all" && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        Showing pages with: {selectedIssueType}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedIssueType("all")}
                    >
                      Show All Pages
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      {/* Page Analysis */}
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Page Analysis
              </CardTitle>
              <CardDescription>
                {selectedIssueType === "all"
                  ? "Detailed analysis of pages with SEO issues"
                  : `Pages with ${selectedIssueType} issues`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {filteredPages.length}{" "}
                {filteredPages.length === 1 ? "page" : "pages"}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {paginatedPages.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 mb-4 rounded-full bg-green-50 dark:bg-green-950/20 flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {selectedIssueType === "all"
                  ? "All Pages Look Great!"
                  : "No Issues Found"}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                {selectedIssueType === "all"
                  ? "All analyzed pages are performing well with no significant SEO issues detected."
                  : `No pages found with "${selectedIssueType}" issues. Try selecting a different issue type or view all pages.`}
              </p>
              {selectedIssueType !== "all" && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setSelectedIssueType("all")}
                >
                  View All Pages
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedPages.map((page: PageAnalysis, index: number) => (
                <div key={index} className="group relative">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-sm transition-all duration-200">
                    {/* Header */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                              {page.title || "Untitled Page"}
                            </h4>
                            <Badge
                              variant={
                                page.issues?.length
                                  ? "destructive"
                                  : "secondary"
                              }
                              className="text-xs shrink-0"
                            >
                              {page.issues?.length || 0}{" "}
                              {page.issues?.length === 1 ? "issue" : "issues"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                              {page.url}
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 shrink-0"
                              asChild
                            >
                              <a
                                href={page.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Issues */}
                    {page.issues && page.issues.length > 0 && (
                      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                        <div className="space-y-3">
                          {page.issues.map(
                            (issue: string, issueIndex: number) => (
                              <div
                                key={issueIndex}
                                className={`p-3 rounded-lg border transition-colors ${
                                  selectedIssueType === issue
                                    ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
                                    : "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 shrink-0">
                                    {getIssueIcon(issue)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h5 className="font-medium text-sm text-slate-900 dark:text-slate-100">
                                        {issue}
                                      </h5>
                                      {selectedIssueType === issue && (
                                        <Badge
                                          variant="default"
                                          className="text-xs"
                                        >
                                          Filtered
                                        </Badge>
                                      )}
                                    </div>
                                    {getSuggestionForIssue(issue) &&
                                      getSuggestionForIssue(issue) !==
                                        "Review this issue and implement best practices to improve your SEO performance." && (
                                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                          ℹ️ {getSuggestionForIssue(issue)}
                                        </p>
                                      )}
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Enhanced Metrics */}
                    <div className="p-4 bg-white dark:bg-slate-900/50">
                      {/* Primary Metrics Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-6 text-xs text-slate-600 dark:text-slate-400">
                          <div
                            className="flex items-center gap-1"
                            title="Content statistics"
                          >
                            <FileText className="h-3 w-3" />
                            <span>
                              {(page.wordCount || 0).toLocaleString()} words
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            title="Reading time"
                          >
                            <Timer className="h-3 w-3" />
                            <span>{page.readingTime || 0} min read</span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            title="Page load time"
                          >
                            <Gauge className="h-3 w-3" />
                            <span>{(page.loadTime || 0).toFixed(1)}s</span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            title="Internal links"
                          >
                            <Link className="h-3 w-3" />
                            <span>{page.internalLinks || 0} internal</span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            title="External links"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>{page.externalLinks || 0} external</span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            title="Images count"
                          >
                            <ImageIcon className="h-3 w-3" />
                            <span>{page.images || 0} images</span>
                          </div>
                        </div>
                      </div>

                      {/* SEO Features Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
                        <div
                          className="flex items-center gap-1"
                          title="Schema.org structured data"
                        >
                          <CheckCircle2
                            className={`h-3 w-3 ${page.hasSchema ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasSchema ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Schema
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Mobile viewport meta tag"
                        >
                          <Smartphone
                            className={`h-3 w-3 ${page.hasMobileViewport ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasMobileViewport ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Mobile
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Image alt attributes"
                        >
                          <ImageIcon
                            className={`h-3 w-3 ${page.hasAltText ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasAltText ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Alt Text
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Canonical URL"
                        >
                          <Link
                            className={`h-3 w-3 ${page.hasCanonical ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasCanonical ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Canonical
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Open Graph meta tags"
                        >
                          <Share2
                            className={`h-3 w-3 ${page.hasOpenGraph ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasOpenGraph ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            OpenGraph
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Twitter Card meta tags"
                        >
                          <Share2
                            className={`h-3 w-3 ${page.hasTwitterCard ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasTwitterCard ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Twitter
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Language declaration"
                        >
                          <Languages
                            className={`h-3 w-3 ${page.hasLanguage ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasLanguage ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            {page.hasLanguage
                              ? page.language || "Lang"
                              : "No Lang"}
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title="Breadcrumb navigation"
                        >
                          <Navigation
                            className={`h-3 w-3 ${page.hasBreadcrumbs ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.hasBreadcrumbs ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            Breadcrumbs
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1"
                          title={`H1 tags count: ${page.h1Count || 0}`}
                        >
                          <Hash
                            className={`h-3 w-3 ${page.h1Count === 1 ? "text-emerald-500" : "text-slate-400"}`}
                          />
                          <span
                            className={`${page.h1Count === 1 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`}
                          >
                            H1: {page.h1Count || 0}
                          </span>
                        </div>

                        {page.externalLinksNoFollow > 0 && (
                          <div
                            className="flex items-center gap-1"
                            title="External links with nofollow"
                          >
                            <ExternalLink className="h-3 w-3 text-blue-500" />
                            <span className="text-blue-600 dark:text-blue-400">
                              {page.externalLinksNoFollow} nofollow
                            </span>
                          </div>
                        )}

                        {page.imagesSizeLarge > 0 && (
                          <div
                            className="flex items-center gap-1"
                            title="Large unoptimized images"
                          >
                            <ImageIcon className="h-3 w-3 text-amber-500" />
                            <span className="text-amber-600 dark:text-amber-400">
                              {page.imagesSizeLarge} large
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredPages.length)}{" "}
                  of {filteredPages.length} pages
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (page) =>
                          Math.abs(page - currentPage) <= 2 ||
                          page === 1 ||
                          page === totalPages,
                      )
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-slate-400 dark:text-slate-500 text-sm">
                              ...
                            </span>
                          )}
                          <Button
                            variant={
                              currentPage === page ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="h-8 w-8 p-0"
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
